import moment from 'moment';
import type { DiscountCardProps } from '@tempmee/react-blocks';
import { deriveDiscountStatus, type IDiscountCode } from '~/domain/DiscountCode';

/** Dates render as the office reads them elsewhere in the portal: 1/15/2026. */
const DISPLAY_DATE_FORMAT = 'M/D/YYYY';

/**
 * Formats an API timestamp for display, or returns `undefined` when there is
 * nothing meaningful to show.
 *
 * Guards Go's zero time as well as a missing value: a `time.Time` field that was
 * never set marshals as `0001-01-01T00:00:00Z`, which would otherwise render as
 * a straight-faced "1/1/1" in the card.
 */
export const formatDisplayDate = (isoDate?: string | null): string | undefined => {
  if (!isoDate) {
    return undefined;
  }

  // Strict ISO parsing: the API only ever sends ISO-8601, and moment's loose
  // mode falls back to `new Date()` with a deprecation warning on anything else.
  const parsed = moment(isoDate, moment.ISO_8601, true);

  if (!parsed.isValid() || parsed.year() <= 1) {
    return undefined;
  }

  return parsed.format(DISPLAY_DATE_FORMAT);
};

/**
 * Maps one discount row — an applied code or a codeless discount — onto the
 * presentational `DiscountCard` contract.
 *
 * The headline is the discount's `campaignName` for every row (GOTU-19168): the
 * raw code stays on the type, but the office reads the discount by its name, and
 * a codeless row has no code to show at all.
 *
 * The card is display-only and hides a row on `undefined`, so every nullable
 * field is normalised from the BE's explicit `null` to `undefined` here: no
 * `usesRemaining` hides the counter, no `endsAt` hides the expiry line, no
 * `appliedBy` hides the name but keeps the date, and an empty `professionTypes`
 * hides that line. `null` happens to satisfy the card's own `!= null` guard, but
 * it does not satisfy its prop types — the normalisation is what keeps that honest.
 *
 * `officeEligible` reaches the card through `deriveDiscountStatus`, which turns a
 * live-but-ineligible row into the `ineligible` chip (added to
 * `DiscountCardStatus` in @tempmee/core 1.16 / react-blocks 0.12) rather than
 * the misleading ACTIVE it used to render as.
 */
export const toDiscountCardProps = (
  code: IDiscountCode,
  now: Date = new Date(),
): DiscountCardProps => ({
  code: code.campaignName,
  description: code.description,
  // A campaign-service older than #229 omits the key; read that as empty, so
  // the card renders without the line instead of throwing.
  professionTypes: (code.professionTypes ?? []).map((professionType) => professionType.label),
  status: deriveDiscountStatus(code, now),
  usesRemaining: code.usesRemaining ?? undefined,
  expiresLabel: formatDisplayDate(code.endsAt),
  // Nobody applied a codeless discount: the BE fills `appliedBy` with "" and
  // `appliedAt` with null, but the row says so explicitly, so branch on that.
  // On a coded row, an applier the identity mirror has not caught up with also
  // comes back as "" — fall through to undefined so the card drops the name row
  // instead of rendering an empty line.
  appliedByLabel: code.codeless ? undefined : code.appliedBy || undefined,
  appliedAtLabel: code.codeless ? undefined : formatDisplayDate(code.appliedAt),
});
